import { ReactNode, useContext, useState, useCallback, useEffect, useRef } from "react";
import { Dropdown, MenuProps, Tooltip, message } from "antd";
import type { DropdownProps } from "antd";
import {
  CameraOutlined,
  BgColorsOutlined,
  EditOutlined,
  BorderOutlined,
  LineOutlined,
  ArrowRightOutlined,
  ColumnWidthOutlined,
  AimOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  PictureOutlined,
  RedoOutlined,
  SwapOutlined,
  FontSizeOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useRecoilState, useRecoilValue } from "recoil";
import { ViewerContext } from "../../page/Viewer";
import { ANNOTATION_LABEL_SCALES, annotationLabelScaleAtom, isDualViewerAtom } from "../../atom";
import { downloadDualCapture, useRegisteredViewers } from "./viewerRegistry";
import styled, { createGlobalStyle } from "styled-components";

interface ViewerContextMenuProps {
  children: ReactNode;
  viewer: any;
  icc: string;
  onChangeIcc: () => void;
  isDemo?: boolean;
  enableContextMenu?: boolean;
}

const ViewerContextMenu = ({
  children,
  viewer,
  icc,
  onChangeIcc,
  isDemo = false,
  enableContextMenu = true,
}: ViewerContextMenuProps) => {
  const { userCustomSet, setUserCustomSet, activePanel, setActivePanel, togglePanel } = useContext(ViewerContext);
  const [dropdownPlacement, setDropdownPlacement] = useState<DropdownProps["placement"]>("bottomLeft");
  const [activeDrawMode, setActiveDrawMode] = useState<string>("MOVE");
  const [menuOffset, setMenuOffset] = useState<[number, number]>([0, 0]);
  const [isOpen, setIsOpen] = useState(false); // viewer context menu state
  const [labelScale, setLabelScale] = useRecoilState(annotationLabelScaleAtom);
  const isDualViewer = useRecoilValue(isDualViewerAtom);
  const registeredViewers = useRegisteredViewers();
  // 듀얼 뷰어에서 양쪽 다 화면이 떠 있을 때만 '양쪽 화면 캡처' 노출
  const canDualCapture = isDualViewer && !!registeredViewers.left && !!registeredViewers.right;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const focusViewerWrapper = useCallback((element?: EventTarget | null) => {
    if (element instanceof HTMLElement) {
      element.focus();
    }
  }, []);

  const isEditableTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;

    return target.closest("input, textarea, select, [contenteditable='true']") !== null;
  }, []);

  const toggleDrawMode = useCallback((modeKey: string) => {
    if (!viewer) return;

    const currentMode = viewer.annotationsStore?.getMode?.() || activeDrawMode;

    switch (modeKey) {
      case "draw-freehand":
        if (currentMode === "DRAW") {
          viewer.stopDrawing?.();
        } else {
          viewer.startDrawing?.();
        }
        break;
      case "draw-rect":
        if (currentMode === "DRAWRECT") {
          viewer.stopDrawRect?.();
        } else {
          viewer.startDrawRect?.();
        }
        break;
      case "draw-ellipse":
        if (currentMode === "DRAWELLIPSE") {
          viewer.stopDrawEllipse?.();
        } else {
          viewer.startDrawEllipse?.();
        }
        break;
      case "draw-arrow":
        if (currentMode === "DRAWARROW") {
          viewer.stopDrawArrow?.();
        } else {
          viewer.startDrawArrow?.();
        }
        break;
      case "draw-ruler":
        if (currentMode === "RULER") {
          viewer.stopRuler?.();
        } else {
          viewer.startRuler?.();
        }
        break;
      default:
        break;
    }
  }, [activeDrawMode, viewer]);

  const stopAllDrawModes = useCallback(() => {
    if (!viewer) return;

    viewer.stopDrawing?.();
    viewer.stopDrawRect?.();
    viewer.stopDrawEllipse?.();
    viewer.stopDrawArrow?.();
    viewer.stopRuler?.();
  }, [viewer]);

  const handleShortcutKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isEditableTarget(e.target)) {
      return;
    }

    if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
      return;
    }

    // 뷰어 좌상단 annotation 툴바의 버튼 순서와 맞춘다 (Move 다음부터 1~5)
    const shortcutMap: Record<string, string> = {
      "1": "draw-ellipse",
      "2": "draw-rect",
      "3": "draw-freehand",
      "4": "draw-ruler",
      "5": "draw-arrow",
    };

    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();

      setIsOpen(false);
      stopAllDrawModes();
      return;
    }

    const actionKey = shortcutMap[e.key];
    if (!actionKey) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();

    setIsOpen(false);
    toggleDrawMode(actionKey);
  }, [isEditableTarget, stopAllDrawModes, toggleDrawMode]);

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }

      if (isEditableTarget(e.target)) {
        return;
      }

      const wrapperElement = wrapperRef.current;
      const activeElement = document.activeElement;
      const isViewerFocused =
        !!wrapperElement &&
        (activeElement === wrapperElement || wrapperElement.contains(activeElement));

      const isDrawModeActive = activeDrawMode !== "MOVE";

      if (!isOpen && !isViewerFocused && !isDrawModeActive) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();

      setIsOpen(false);
      stopAllDrawModes();
      wrapperElement?.focus();
    };

    window.addEventListener("keydown", handleEscapeKey, true);
    return () => {
      window.removeEventListener("keydown", handleEscapeKey, true);
    };
  }, [activeDrawMode, isEditableTarget, isOpen, stopAllDrawModes]);

  // annotation 라벨 글씨 배율을 뷰어에 반영
  useEffect(() => {
    viewer?.setAnnotationLabelScale?.(labelScale);
  }, [viewer, labelScale]);

  // Store의 모드 변경 및 activity 감지
  useEffect(() => {
    if (!viewer || !viewer.annotationsStore) return;

    const handleStoreChange = () => {
      const currentMode = viewer.annotationsStore?.getMode?.() || "MOVE";
      setActiveDrawMode(currentMode);

      // 어노테이션 그리기가 시작되면 메뉴 닫기
      const isActivityInProgress = viewer.annotationsStore?.isActivityInProgress?.();
      if (isActivityInProgress && currentMode !== "MOVE") {
        setIsOpen(false);
      }
    };

    // 초기 모드 설정
    handleStoreChange();

    // Store의 CHANGE_EVENT 리스너 등록
    viewer.annotationsStore?.addHandler?.("CHANGE_EVENT", handleStoreChange);

    return () => {
      viewer.annotationsStore?.removeHandler?.("CHANGE_EVENT", handleStoreChange);
    };
  }, [viewer]);

  // 우클릭 위치에 따라 메뉴 방향 결정
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); // 브라우저 기본 컨텍스트 메뉴 비활성화

    focusViewerWrapper(e.currentTarget);

    // 캡처 항목·라벨 크기 행 추가분 반영
    const menuHeight = canDualCapture ? 610 : 530;
    const menuWidth = 220;
    const viewerElement = e.currentTarget as HTMLElement;
    const viewerRect = viewerElement.getBoundingClientRect();
    const clickX = e.clientX;
    const clickY = e.clientY;

    // 뷰어 상단/하단/좌우까지의 거리
    const spaceAbove = clickY - viewerRect.top;
    const spaceBelow = viewerRect.bottom - clickY;
    const spaceLeft = clickX - viewerRect.left;
    const spaceRight = viewerRect.right - clickX;
    const viewerHeight = viewerRect.height;

    // Y축 조정
    let offsetY = 0;
    let placementY = "bottom";

    // 아래 공간이 충분하면 아래로 표시
    if (spaceBelow >= menuHeight) {
      placementY = "bottom";
      offsetY = 0;
    }
    // 위 공간이 충분하면 위로 표시
    else if (spaceAbove >= menuHeight) {
      placementY = "top";
      offsetY = 0;
    }
    // 아래/위 모두 부족하면 뷰어 내에서 최대한 표시
    else {
      if (menuHeight > viewerHeight) {
        placementY = "bottom";
        offsetY = 0;
      } else {
        const overflow = menuHeight - spaceBelow;
        offsetY = -overflow;
        placementY = "bottom";
      }
    }

    // X축 조정
    let offsetX = 0;
    let placementX = "Left";

    if (spaceRight >= menuWidth) {
      placementX = "Left";
      offsetX = 0;
    }
    else if (spaceLeft >= menuWidth) {
      placementX = "Right";
      offsetX = 0;
    }
    else {
      placementX = "Left";
      const overflow = menuWidth - spaceRight;
      offsetX = -overflow;
    }

    setDropdownPlacement(`${placementY}${placementX}` as DropdownProps["placement"]);
    setMenuOffset([offsetX, offsetY]);
  }, [focusViewerWrapper, canDualCapture]);

  // 어노테이션 포함 캡처인데 어노테이션을 그리지 못하고 이미지만 저장된 경우 알림 (원인은 콘솔 경고 참고)
  const warnIfNoAnnotations = (result?: { annotationsDrawn?: boolean } | null) => {
    if (result && !result.annotationsDrawn) {
      message.warning("어노테이션을 그리지 못해 이미지만 캡처되었습니다.");
    }
  };

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      // 어노테이션 모드
      case "draw-freehand":
        if (activeDrawMode === "DRAW") {
          viewer?.stopDrawing?.(); // 토글링 - 선택 헤제
        } else {
          viewer?.startDrawing?.(); // 토글링- 선택 
        }
        break;
      case "draw-rect":
        if (activeDrawMode === "DRAWRECT") {
          viewer?.stopDrawRect?.();
        } else {
          viewer?.startDrawRect?.();
        }
        break;
      case "draw-ellipse":
        if (activeDrawMode === "DRAWELLIPSE") {
          viewer?.stopDrawEllipse?.();
        } else {
          viewer?.startDrawEllipse?.();
        }
        break;
      case "draw-arrow":
        if (activeDrawMode === "DRAWARROW") {
          viewer?.stopDrawArrow?.();
        } else {
          viewer?.startDrawArrow?.();
        }
        break;
      case "draw-ruler":
        if (activeDrawMode === "RULER") {
          viewer?.stopRuler?.();
        } else {
          viewer?.startRuler?.();
        }
        break;

      // 어노테이션 보기 토글
      case "toggle-annotation":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useAnnotation: !prev.useAnnotation,
        }));
        break;

      // 이미지 캡처
      case "capture":
        if (viewer) {
          try {
            let canvas;
            // captureBtn.js 기능과 동일하게 추가 (버튼 렌더링이 안되면 캡처가 안되는 이슈 때문)
            // scalebarInstance가 있으면 스케일바와 함께 캡처, 없으면 순수 이미지만 캡처
            if (viewer.scalebarInstance && viewer.scalebarInstance.getImageWithScalebarAsCanvas) {
              canvas = viewer.scalebarInstance.getImageWithScalebarAsCanvas();
            } else {
              // 스케일바 없이 순수 이미지만 캡처
              canvas = viewer.drawer.canvas;

              // 현재 캔버스를 복사
              const captureCanvas = document.createElement('canvas');
              captureCanvas.width = canvas.width;
              captureCanvas.height = canvas.height;

              const captureContext = captureCanvas.getContext('2d');
              if (captureContext) {
                captureContext.drawImage(canvas, 0, 0);
                canvas = captureCanvas;
              }
            }

            // Blob 방식으로 변경 (base64 URL 길이 문제 해결)
            canvas.toBlob((blob: Blob | null) => {
              if (blob) {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'capture.png';
                link.click();

                // 메모리 해제
                setTimeout(() => URL.revokeObjectURL(url), 100);
              }
            }, 'image/png');
          } catch (error) {
            console.error('Capture failed:', error);
          }
        }
        break;

      // 어노테이션·라벨 포함 캡처
      case "capture-with-annotation":
        Promise.resolve(viewer?.downloadCapture?.({ withAnnotations: true })).then(warnIfNoAnnotations);
        break;

      // 듀얼 뷰어 양쪽 화면 나란히 캡처
      case "capture-dual":
        downloadDualCapture();
        break;
      case "capture-dual-with-annotation":
        downloadDualCapture({ withAnnotations: true }).then(warnIfNoAnnotations);
        break;

      // ICC 토글
      case "toggle-icc":
        onChangeIcc();
        break;

      // 스크롤 방향 반전 토글
      case "toggle-invert-scroll":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useInvertedZoom: !prev.useInvertedZoom,
        }));
        break;

      // UI 요소 토글
      case "toggle-scalebar":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useScaleBar: !prev.useScaleBar,
        }));
        break;
      case "toggle-magnification":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useMagnificationBar: !prev.useMagnificationBar,
        }));
        break;
      case "toggle-thumbnail":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useViewerThumbnail: !prev.useViewerThumbnail,
        }));
        break;
      case "toggle-rotation":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useViewerRotationSlider: !prev.useViewerRotationSlider,
        }));
        break;
      case "toggle-gamma":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useGammaSlider: !prev.useGammaSlider,
        }));
        break;
      case "toggle-capture-btn":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useCaptureButton: !prev.useCaptureButton,
        }));
        break;
      case "toggle-icc-btn":
        setUserCustomSet((prev: any) => ({
          ...prev,
          useICCButton: !prev.useICCButton,
        }));
        break;

      // 패널 토글
      case "toggle-info-panel":
        togglePanel?.("info");
        break;
      case "toggle-annotation-panel":
        togglePanel?.("annotation");
        break;
      case "toggle-comment-panel":
        togglePanel?.("comment");
        break;

      default:
        break;
    }
  };

  const CircleIcon = () => (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="8" />
    </svg>
  );

  // helper function
  const getActiveStyle = (isActive: boolean) =>
    isActive ? { backgroundColor: "#e6f7ff", borderLeft: "3px solid #bae7ff" } : {};

  const getGroupItemActiveStyle = (isActive: boolean) =>
    isActive
      ? { backgroundColor: "#e6f7ff", borderLeft: "3px solid #bae7ff", margin: "0", paddingLeft: "12px" }
      : { margin: "0" };

  const annotationTools = (
    <AnnotationToolsRow>
      <Tooltip title="DrawEllipse (1)"><ToolIcon $active={activeDrawMode === "DRAWELLIPSE"} onClick={() => handleMenuClick({ key: "draw-ellipse" } as any)}><CircleIcon /></ToolIcon></Tooltip>
      <Tooltip title="DrawRect (2)"><ToolIcon $active={activeDrawMode === "DRAWRECT"} onClick={() => handleMenuClick({ key: "draw-rect" } as any)}><BorderOutlined /></ToolIcon></Tooltip>
      <Tooltip title="Draw (3)"><ToolIcon $active={activeDrawMode === "DRAW"} onClick={() => handleMenuClick({ key: "draw-freehand" } as any)}><EditOutlined /></ToolIcon></Tooltip>
      <Tooltip title="Ruler (4)"><ToolIcon $active={activeDrawMode === "RULER"} onClick={() => handleMenuClick({ key: "draw-ruler" } as any)}><ColumnWidthOutlined /></ToolIcon></Tooltip>
      <Tooltip title="DrawArrow (5)"><ToolIcon $active={activeDrawMode === "DRAWARROW"} onClick={() => handleMenuClick({ key: "draw-arrow" } as any)}><ArrowRightOutlined /></ToolIcon></Tooltip>
    </AnnotationToolsRow>
  );

  // 라벨 글씨 크기: 기본 크기(100%)가 최소
  const labelScaleIndex = Math.max(0, ANNOTATION_LABEL_SCALES.indexOf(labelScale));
  const changeLabelScale = (e: React.MouseEvent, delta: number) => {
    e.stopPropagation();
    const nextIndex = Math.min(
      ANNOTATION_LABEL_SCALES.length - 1,
      Math.max(0, labelScaleIndex + delta)
    );
    setLabelScale(ANNOTATION_LABEL_SCALES[nextIndex]);
  };

  const labelScaleControl = (
    <LabelScaleRow onClick={(e) => e.stopPropagation()}>
      <span>라벨 글씨 크기</span>
      <LabelScaleStepper>
        <ToolIcon
          as="button"
          type="button"
          aria-label="라벨 글씨 작게"
          disabled={labelScaleIndex === 0}
          onClick={(e: React.MouseEvent) => changeLabelScale(e, -1)}
        >
          <MinusOutlined />
        </ToolIcon>
        <LabelScaleValue>{Math.round(labelScale * 100)}%</LabelScaleValue>
        <ToolIcon
          as="button"
          type="button"
          aria-label="라벨 글씨 크게"
          disabled={labelScaleIndex === ANNOTATION_LABEL_SCALES.length - 1}
          onClick={(e: React.MouseEvent) => changeLabelScale(e, 1)}
        >
          <PlusOutlined />
        </ToolIcon>
      </LabelScaleStepper>
    </LabelScaleRow>
  );

  const items: MenuProps["items"] = [
    ...(!isDemo ? [
      {
        key: "annotation-tools",
        label: annotationTools,
        style: { padding: "0", cursor: "default", margin: "0" },
        className: "annotation-tools-item",
      },
      { type: "divider" as const },
    ] : []),
    {
      key: "toggle-info-panel",
      label: "정보",
      icon: <InfoCircleOutlined />,
      style: getActiveStyle(activePanel === "info"),
    },
    {
      key: "toggle-annotation-panel",
      label: "어노테이션 목록",
      icon: <EditOutlined />,
      style: getActiveStyle(activePanel === "annotation"),
    },
    ...(!isDemo ? [
      {
        key: "toggle-comment-panel",
        label: "코멘트 목록",
        icon: <MessageOutlined />,
        style: getActiveStyle(activePanel === "comment"),
      },
    ] : []),
    {
      type: "divider",
    },
    {
      key: "capture",
      label: "이미지 캡처",
      icon: <CameraOutlined />,
    },
    {
      key: "capture-with-annotation",
      label: "어노테이션 포함 캡처",
      icon: <CameraOutlined />,
    },
    ...(canDualCapture
      ? [
          {
            key: "capture-dual",
            label: "양쪽 화면 캡처",
            icon: <CameraOutlined />,
          },
          {
            key: "capture-dual-with-annotation",
            label: "양쪽 화면 캡처 (어노테이션 포함)",
            icon: <CameraOutlined />,
          },
        ]
      : []),
    {
      type: "divider",
    },
    {
      key: "toggle-icc",
      label: "ICC 프로필",
      icon: <BgColorsOutlined />,
      style: getActiveStyle(icc !== "none"),
    },
    {
      type: "divider",
    },
    {
      key: "toggle-invert-scroll",
      label: "스크롤 방향 반전",
      icon: <SwapOutlined />,
      style: getActiveStyle(userCustomSet?.useInvertedZoom),
    },
    ...(!isDemo ? [
      { type: "divider" as const },
      {
      key: "ui-group",
      label: "뷰어 설정",
      type: "group" as const,
      children: [
        {
          key: "toggle-annotation",
          label: "어노테이션",
          icon: <EditOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useAnnotation),
        },
        {
          key: "label-scale",
          label: labelScaleControl,
          icon: <FontSizeOutlined />,
          style: { margin: "0", cursor: "default" },
        },
        {
          key: "toggle-icc-btn",
          label: "ICC 버튼",
          icon: <BgColorsOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useICCButton),
        },
        {
          key: "toggle-scalebar",
          label: "스케일 표시",
          icon: <LineOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useScaleBar),
        },
        {
          key: "toggle-magnification",
          label: "배율 표시",
          icon: <AimOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useMagnificationBar),
        },
        {
          key: "toggle-thumbnail",
          label: "썸네일",
          icon: <PictureOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useViewerThumbnail),
        },
        {
          key: "toggle-rotation",
          label: "회전 슬라이더",
          icon: <RedoOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useViewerRotationSlider),
        },
        {
          key: "toggle-gamma",
          label: "감마 슬라이더",
          icon: <FilterOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useGammaSlider),
        },
        {
          key: "toggle-capture-btn",
          label: "캡처 버튼",
          icon: <CameraOutlined />,
          style: getGroupItemActiveStyle(userCustomSet?.useCaptureButton),
        },
      ],
    }] : []),
  ];


  // 휠 이벤트 시 컨텍스트 메뉴 닫기
  const handleWheel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const content = (
    <StyledWrapper
      ref={wrapperRef}
      tabIndex={0}
      onContextMenu={enableContextMenu ? handleContextMenu : undefined}
      onMouseDownCapture={(e) => focusViewerWrapper(e.currentTarget)}
      onKeyDownCapture={handleShortcutKeyDown}
      onWheel={handleWheel}
    >
      {children}
    </StyledWrapper>
  );

  if (!enableContextMenu) {
    return content;
  }

  return (
    <>
      <GlobalDropdownStyle />
      <Dropdown
        menu={{ items, onClick: handleMenuClick }}
        trigger={["contextMenu"]}
        placement={dropdownPlacement}
        getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        autoAdjustOverflow={false}
        align={{
          offset: menuOffset,
          overflow: { adjustX: false, adjustY: false },
        }}
        open={isOpen}
        onOpenChange={setIsOpen}
        popupRender={(menu) => (
          <div onContextMenu={(e) => e.preventDefault()}>
            {menu}
          </div>
        )}
      >
        {content}
      </Dropdown>
    </>
  );
};

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  outline: none;
`;

const AnnotationToolsRow = styled.div`
  display: flex;
  gap: 6px;
  padding: 2px;
  justify-content: center;
  align-items: center;
`;

const ToolIcon = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  border-bottom: ${({ $active }) => ($active ? "3px solid #1890ff" : "3px solid transparent")};
  background-color: ${({ $active }) => ($active ? "#e6f7ff" : "transparent")};
  border-radius: ${({ $active }) => ($active ? "4px 4px 0 0" : "4px")};

  &:hover {
    background-color: #bae7ff;
    color: #1890ff;
  }
`;

const LabelScaleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const LabelScaleStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  button {
    width: 22px;
    height: 22px;
    font-size: 12px;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.35;
    background: transparent;
  }
`;

const LabelScaleValue = styled.span`
  min-width: 38px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const GlobalDropdownStyle = createGlobalStyle`
  .ant-dropdown-menu {
    height: auto !important; // 기본 높이는 자동으로 설정하되 
    max-height: 490px !important; // 멕시멈 높이는 490px로 제한
    overflow-y: visible !important;
    overflow-x: hidden !important;
    min-width: 220px !important;
    border-radius: 4px !important;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.15) !important;

    /* 스크롤바 스타일링 */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }

  .ant-dropdown-menu .ant-dropdown-menu-item.annotation-tools-item {
    padding: 0 !important;
    margin: 0 !important;
  }

  .ant-dropdown-menu-item-only-child.annotation-tools-item {
    padding: 0 !important;
  }

  .ant-dropdown-menu-item {
    border-radius: 0 !important;
    font-size: 11px !important;
    padding: 4px !important;
    margin: 0 !important;
  }

  .ant-dropdown-menu-item-group-list {
    margin: 0 !important;
    padding: 0 !important;

    .ant-dropdown-menu-item {
      margin: 1px !important;
      padding: 4px 12px !important;
    }
  }

  .ant-dropdown-menu-item-group-title {
    font-size: 10px !important;
    padding: 2px 4px !important;
  }

  .ant-dropdown-menu-item:hover,
  .ant-dropdown-menu-submenu-title:hover {
    background-color: #bae7ff !important;
  }

  .annotation-tools-item {
    &:hover {
      background-color: transparent !important;
    }
  }
`;

export default ViewerContextMenu;
